// Item 97 — Multi-tab dashboards: the single source of truth for the tab model, shared by the
// Builder, the public Viewer, the Tabs manager and the AI tab operations.
//
// Mirrors backend/tabs.py exactly. A dashboard with no `tabs` array behaves as one implicit
// "Overview" tab, so legacy dashboards render unchanged before/after the m0011 migration.
import { newWid } from "@/lib/builderOps";

export const BAR_POSITIONS = [
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "left", label: "Left (vertical)" },
  { value: "right", label: "Right (vertical)" },
];
export const BAR_STYLES = [
  { value: "underline", label: "Underline" },
  { value: "pill", label: "Pill" },
  { value: "segmented", label: "Segmented" },
  { value: "button", label: "Button" },
];
export const OVERFLOW_MODES = [
  { value: "scroll", label: "Scroll" },
  { value: "wrap", label: "Wrap to next line" },
  { value: "dropdown", label: "Overflow dropdown" },
];
export const MOBILE_MODES = [
  { value: "scroll", label: "Swipe / scroll" },
  { value: "dropdown", label: "Dropdown" },
  { value: "accordion", label: "Accordion list" },
];
export const BADGE_KINDS = [
  { value: "none", label: "None" },
  { value: "dot", label: "Status dot" },
  { value: "count", label: "Count" },
  { value: "text", label: "Text" },
];
export const TAB_ICONS = [
  "layout-dashboard", "bar-chart-3", "line-chart", "pie-chart", "trending-up", "coins", "wallet",
  "package", "boxes", "warehouse", "truck", "shopping-cart", "store", "users", "user-check",
  "map-pin", "globe", "calendar-check", "clipboard-list", "target", "percent", "factory", "gauge",
];

export const DEFAULT_BAR = {
  position: "top", style: "underline", overflow: "scroll", mobile: "scroll", remember: "last",
  show: true, cross_tab_interactions: false, searchable: true,
  height: 38, width: 180, align: "start", gap: 6, divider: true, radius: 10, padding: 6,
  bg: "", border: "", shadow: false, transparent: false,
  font_family: "", font_size: 12, font_weight: 500, italic: false, underline: false,
  text: "", selected_text: "", hover_text: "", selected_bg: "", hover_bg: "", disabled_text: "",
  indicator: "", indicator_size: 2, indicator_position: "bottom",
};

const DEFAULT_VIS = { mode: "everyone", user_emails: [], group_ids: [], roles: [], rules: [] };

export function normalizeBar(canvas) {
  const b = (canvas && canvas.tabs_bar) || {};
  const out = { ...DEFAULT_BAR };
  Object.keys(DEFAULT_BAR).forEach((k) => { if (b[k] !== undefined && b[k] !== null) out[k] = b[k]; });
  return out;
}

export function normalizeTab(t, order, taken = []) {
  const src = t || {};
  const name = String(src.name || "").trim() || `Tab ${order + 1}`;
  return {
    id: String(src.id || "").trim() || newTabId(taken, name),
    name: name.slice(0, 60),
    order: Number.isInteger(src.order) ? src.order : order,
    icon: String(src.icon || ""),
    icon_url: String(src.icon_url || ""),
    badge: { kind: (src.badge || {}).kind || "none", value: String((src.badge || {}).value || ""), color: (src.badge || {}).color || "" },
    hidden: !!src.hidden,
    disabled: !!src.disabled,
    locked: !!src.locked,
    default: !!src.default,
    canvas: src.canvas && typeof src.canvas === "object" ? src.canvas : {},
    visibility: { ...DEFAULT_VIS, ...(src.visibility || {}) },
    // Item 32: tabs created from an existing dashboard remember their source
    linked_dashboard_id: src.linked_dashboard_id || null,
    linked_name: src.linked_name || null,
    linked_at: src.linked_at || null,
  };
}

export function newTabId(taken = [], name = "") {
  const slug = String(name || "tab").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 24) || "tab";
  let cand = `t_${slug}`, i = 2;
  while (taken.includes(cand)) { cand = `t_${slug}_${i}`; i += 1; }
  return cand;
}

export function normalizeTabs(dash) {
  const raw = dash && Array.isArray(dash.tabs) ? dash.tabs : null;
  const taken = [];
  let out;
  if (raw && raw.length) {
    out = raw.slice(0, 60).map((t, i) => { const nt = normalizeTab(t, i, taken); taken.push(nt.id); return nt; });
  } else {
    out = [normalizeTab({ id: "t_overview", name: "Overview", default: true }, 0, taken)];
  }
  out.sort((a, b) => a.order - b.order);
  out = out.map((t, i) => ({ ...t, order: i }));
  if (!out.some((t) => t.default)) out[0] = { ...out[0], default: true };
  return out;
}

export function defaultTabId(tabs) {
  return (tabs.find((t) => t.default && !t.hidden) || tabs.find((t) => !t.hidden) || tabs[0] || {}).id || "t_overview";
}

export function widgetTabId(w, tabs) {
  const ids = new Set(tabs.map((t) => t.id));
  return w && w.tab_id && ids.has(w.tab_id) ? w.tab_id : (tabs[0] || {}).id;
}

export function widgetsOfTab(widgets, tabs, tabId) {
  return (widgets || []).filter((w) => widgetTabId(w, tabs) === tabId);
}

export function tabWidgetCounts(widgets, tabs) {
  const out = {};
  tabs.forEach((t) => { out[t.id] = 0; });
  (widgets || []).forEach((w) => {
    if (w.parent_id) return;
    const id = widgetTabId(w, tabs);
    out[id] = (out[id] || 0) + 1;
  });
  return out;
}

// Per-tab canvas overrides (background, wallpaper, grid, layout mode) layered on the dashboard canvas.
export function tabCanvas(canvas, tab) {
  const o = (tab && tab.canvas) || {};
  const keys = ["bg_color", "wallpaper_url", "wallpaper_opacity", "layout_mode", "grid_cols"];
  const patch = {};
  keys.forEach((k) => { if (o[k] !== undefined && o[k] !== "" && o[k] !== null) patch[k] = o[k]; });
  return { ...canvas, ...patch };
}

// ---------------------------------------------------------------- conditional visibility (§11)
function cmp(op, a, b) {
  const s = (x) => (x === null || x === undefined ? "" : String(x).toLowerCase());
  const n = (x) => (typeof x === "number" ? x : parseFloat(x));
  switch (op) {
    case "eq": return Array.isArray(a) ? a.map(s).includes(s(b)) : s(a) === s(b);
    case "neq": return !(Array.isArray(a) ? a.map(s).includes(s(b)) : s(a) === s(b));
    case "in": {
      const list = (Array.isArray(b) ? b : String(b || "").split(",")).map(s);
      return Array.isArray(a) ? a.map(s).some((x) => list.includes(x)) : list.includes(s(a));
    }
    case "contains": return s(a).includes(s(b));
    case "gt": return n(a) > n(b);
    case "gte": return n(a) >= n(b);
    case "lt": return n(a) < n(b);
    case "lte": return n(a) <= n(b);
    case "set": return Array.isArray(a) ? a.length > 0 : a !== "" && a !== null && a !== undefined;
    case "unset": return Array.isArray(a) ? a.length === 0 : a === "" || a === null || a === undefined;
    default: return true;
  }
}

/** Rule-based visibility, evaluated in the browser because it depends on live filter values.
 *  ctx = { gfVals, params, role }. Principal-based visibility is enforced server-side. */
export function tabRulesPass(tab, ctx = {}) {
  const rules = ((tab || {}).visibility || {}).rules || [];
  if (!rules.length) return true;
  return rules.every((r) => {
    const val = r.source === "param" ? (ctx.params || {})[r.key] : (ctx.gfVals || {})[r.key];
    return cmp(r.op || "eq", val && typeof val === "object" && !Array.isArray(val) ? val.value : val, r.value);
  });
}

export function openableTabs(tabs, ctx = {}, isAuthor = false) {
  const list = isAuthor ? tabs : tabs.filter((t) => !t.hidden && tabRulesPass(t, ctx));
  return list.length ? list : tabs.filter((t) => !t.hidden).slice(0, 1);
}

// ---------------------------------------------------------------- tab state persistence (§12)
const KEY = (dashId) => `eip_tab_${dashId}`;
export function rememberTab(dashId, tabId) {
  try { localStorage.setItem(KEY(dashId), tabId); } catch { /* private mode */ }
}
export function recallTab(dashId) {
  try { return localStorage.getItem(KEY(dashId)) || ""; } catch { return ""; }
}
export function initialTabId(dashId, tabs, bar, urlTab = "") {
  const byUrl = urlTab && tabs.find((t) => t.id === urlTab || t.name.toLowerCase() === String(urlTab).toLowerCase());
  if (byUrl && !byUrl.hidden) return byUrl.id;
  if ((bar.remember || "last") === "last") {
    const last = recallTab(dashId);
    if (last && tabs.some((t) => t.id === last && !t.hidden)) return last;
  }
  return defaultTabId(tabs);
}

const SCROLL_KEY = (dashId, tabId) => `eip_tabscroll_${dashId}_${tabId}`;
export const saveTabScroll = (dashId, tabId, y) => {
  try { sessionStorage.setItem(SCROLL_KEY(dashId, tabId), String(Math.round(y || 0))); } catch { /* noop */ }
};
export const readTabScroll = (dashId, tabId) => {
  try { return Number(sessionStorage.getItem(SCROLL_KEY(dashId, tabId)) || 0) || 0; } catch { return 0; }
};

// ---------------------------------------------------------------- tab operations (UI + AI §16)
function resolveTab(tabs, ref) {
  const want = String(ref || "").trim().toLowerCase();
  return tabs.find((t) => t.id.toLowerCase() === want) || tabs.find((t) => t.name.toLowerCase() === want) || null;
}

/** Applies a list of operations to {tabs, widgets}. Used by the Tabs manager AND by AI tab-ops,
 *  so an AI-created tab is structurally identical to a hand-made one.
 *  Returns { tabs, widgets, log: [string], activeHint } */
export function applyTabOps(tabsIn, widgetsIn, ops, cols = 12) {
  let tabs = tabsIn.map((t) => ({ ...t }));
  let widgets = widgetsIn.map((w) => ({ ...w }));
  const log = [];
  let activeHint = "";

  const reindex = () => { tabs = tabs.map((t, i) => ({ ...t, order: i })); };

  for (const op of ops || []) {
    const kind = op.op;
    if (kind === "create_tab") {
      const name = String(op.name || "New tab").trim().slice(0, 60);
      if (tabs.some((t) => t.name.toLowerCase() === name.toLowerCase())) { log.push(`"${name}" already exists`); continue; }
      const tab = normalizeTab({ name, icon: op.icon || "" }, tabs.length, tabs.map((t) => t.id));
      const afterIdx = op.after ? tabs.findIndex((t) => t.id === op.after || t.name === op.after) : -1;
      if (afterIdx >= 0) tabs.splice(afterIdx + 1, 0, tab); else tabs.push(tab);
      reindex();
      activeHint = tab.id;
      log.push(`Added tab "${name}"`);
      continue;
    }
    const target = resolveTab(tabs, op.tab);
    if (kind === "rename_tab" && target) {
      const name = String(op.name || "").trim().slice(0, 60);
      if (!name) continue;
      if (tabs.some((t) => t.id !== target.id && t.name.toLowerCase() === name.toLowerCase())) { log.push(`"${name}" already exists`); continue; }
      tabs = tabs.map((t) => (t.id === target.id ? { ...t, name } : t));
      log.push(`Renamed to "${name}"`);
      continue;
    }
    if (kind === "patch_tab" && target) {
      tabs = tabs.map((t) => (t.id === target.id ? normalizeTab({ ...t, ...(op.patch || {}) }, t.order, []) : t));
      continue;
    }
    if (kind === "duplicate_tab" && target) {
      const base = String(op.name || `${target.name} copy`).trim().slice(0, 60);
      let name = base, i = 2;
      while (tabs.some((t) => t.name.toLowerCase() === name.toLowerCase())) { name = `${base} ${i}`; i += 1; }
      const tab = normalizeTab({ ...target, id: "", name, default: false }, tabs.length, tabs.map((t) => t.id));
      const idx = tabs.findIndex((t) => t.id === target.id);
      tabs.splice(idx + 1, 0, tab);
      reindex();
      const src = widgets.filter((w) => widgetTabId(w, tabsIn) === target.id);
      const idMap = {};
      const copies = src.map((w) => {
        const copy = JSON.parse(JSON.stringify(w));
        copy.id = newWid();
        idMap[w.id] = copy.id;
        copy.tab_id = tab.id;
        return copy;
      });
      copies.forEach((c) => { if (c.parent_id && idMap[c.parent_id]) c.parent_id = idMap[c.parent_id]; });
      widgets = [...widgets, ...copies];
      activeHint = tab.id;
      log.push(`Duplicated "${target.name}" as "${name}" with ${copies.length} widget(s)`);
      continue;
    }
    if (kind === "delete_tab" && target) {
      if (tabs.length <= 1) { log.push("A dashboard needs at least one tab"); continue; }
      const gone = widgets.filter((w) => widgetTabId(w, tabsIn) === target.id).length;
      widgets = widgets.filter((w) => widgetTabId(w, tabsIn) !== target.id);
      tabs = tabs.filter((t) => t.id !== target.id);
      reindex();
      if (!tabs.some((t) => t.default)) tabs[0] = { ...tabs[0], default: true };
      activeHint = defaultTabId(tabs);
      log.push(`Deleted "${target.name}" and ${gone} widget(s)`);
      continue;
    }
    if (kind === "merge_tabs") {
      const from = resolveTab(tabs, op.from);
      const into = resolveTab(tabs, op.into);
      if (!from || !into || from.id === into.id || tabs.length <= 1) continue;
      widgets = widgets.map((w) => (widgetTabId(w, tabsIn) === from.id ? { ...w, tab_id: into.id } : w));
      tabs = tabs.filter((t) => t.id !== from.id);
      reindex();
      if (!tabs.some((t) => t.default)) tabs[0] = { ...tabs[0], default: true };
      activeHint = into.id;
      log.push(`Merged "${from.name}" into "${into.name}"`);
      continue;
    }
    if (kind === "reorder_tabs") {
      const order = (op.order || []).map((r) => (resolveTab(tabs, r) || {}).id).filter(Boolean);
      const rest = tabs.filter((t) => !order.includes(t.id));
      tabs = [...order.map((id) => tabs.find((t) => t.id === id)), ...rest];
      reindex();
      log.push("Reordered tabs");
      continue;
    }
    if (kind === "set_default_tab" && target) {
      tabs = tabs.map((t) => ({ ...t, default: t.id === target.id }));
      log.push(`"${target.name}" is now the landing tab`);
      continue;
    }
    if ((kind === "move_widget" || kind === "copy_widget") && target) {
      const w = widgets.find((x) => x.id === op.widget)
        || widgets.find((x) => String(x.title || "").toLowerCase() === String(op.widget_title || op.widget || "").toLowerCase());
      if (!w) continue;
      const family = [w, ...widgets.filter((x) => x.parent_id === w.id)];
      if (kind === "move_widget") {
        const ids = new Set(family.map((x) => x.id));
        widgets = widgets.map((x) => (ids.has(x.id) ? { ...x, tab_id: target.id } : x));
        log.push(`Moved "${w.title || w.type}" to "${target.name}"`);
      } else {
        const idMap = {};
        const maxY = widgets.filter((x) => widgetTabId(x, tabsIn) === target.id && !x.parent_id)
          .reduce((m, x) => Math.max(m, (x.layout?.y || 0) + (x.layout?.h || 4)), 0);
        const copies = family.map((x) => {
          const c = JSON.parse(JSON.stringify(x));
          c.id = newWid();
          idMap[x.id] = c.id;
          c.tab_id = target.id;
          if (!c.parent_id) c.layout = { ...(c.layout || {}), x: Math.min(c.layout?.x || 0, Math.max(0, cols - (c.layout?.w || 6))), y: maxY };
          return c;
        });
        copies.forEach((c) => { if (c.parent_id && idMap[c.parent_id]) c.parent_id = idMap[c.parent_id]; });
        widgets = [...widgets, ...copies];
        log.push(`Copied "${w.title || w.type}" to "${target.name}"`);
      }
      activeHint = target.id;
      continue;
    }
  }
  return { tabs, widgets, log, activeHint };
}

// ---------------------------------------------------------------- bar styling
export function barVars(bar) {
  const px = (v, d) => `${Number.isFinite(Number(v)) ? Number(v) : d}px`;
  return {
    "--tb-height": px(bar.height, 38),
    "--tb-width": px(bar.width, 180),
    "--tb-gap": px(bar.gap, 6),
    "--tb-radius": px(bar.radius, 10),
    "--tb-pad": px(bar.padding, 6),
    "--tb-bg": bar.transparent ? "transparent" : (bar.bg || "var(--glass-bg)"),
    "--tb-border": bar.border || "var(--glass-border)",
    "--tb-font": px(bar.font_size, 12),
    "--tb-weight": String(bar.font_weight || 500),
    "--tb-family": bar.font_family || "inherit",
    "--tb-text": bar.text || "var(--ink-3)",
    "--tb-sel-text": bar.selected_text || "var(--ink)",
    "--tb-hover-text": bar.hover_text || "var(--ink)",
    "--tb-sel-bg": bar.selected_bg || "var(--surface-2)",
    "--tb-hover-bg": bar.hover_bg || "var(--surface-2)",
    "--tb-disabled": bar.disabled_text || "var(--ink-3)",
    "--tb-ind": bar.indicator || "var(--brand-ocean)",
    "--tb-ind-size": px(bar.indicator_size, 2),
  };
}

export function validateTabsClient(tabs, widgets) {
  const issues = [];
  const seen = new Map();
  tabs.forEach((t) => {
    const k = t.name.trim().toLowerCase();
    if (!t.name.trim()) issues.push({ severity: "error", tab_id: t.id, message: "A tab has no name." });
    if (seen.has(k)) issues.push({ severity: "error", tab_id: t.id, message: `Two tabs are called "${t.name}".` });
    seen.set(k, t.id);
    const v = t.visibility || {};
    if (v.mode === "restricted" && !(v.user_emails?.length || v.group_ids?.length || v.roles?.length)) {
      issues.push({ severity: "warning", tab_id: t.id, message: `"${t.name}" is restricted but nobody is allowed.` });
    }
  });
  if (!tabs.some((t) => t.default && !t.hidden)) issues.push({ severity: "warning", tab_id: "", message: "The default (landing) tab is hidden." });
  if (!tabs.some((t) => !t.hidden)) issues.push({ severity: "error", tab_id: "", message: "Every tab is hidden." });
  const counts = tabWidgetCounts(widgets, tabs);
  tabs.forEach((t) => { if (!counts[t.id]) issues.push({ severity: "info", tab_id: t.id, message: `"${t.name}" has no widgets yet.` }); });
  return issues;
}
